import React, { useState, useMemo, useEffect, useRef } from "react";
import { Box, MenuItem, Select, Typography, Paper, CircularProgress, IconButton } from "@mui/material";
import { format, isSameDay, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { JobCard, getTypeColor } from "./JobsWidgetWithWeek";
import { CalendarDateSelector, useCalendarDateSelectorState, useSetCalendarDateSelector } from "../CalendarDateSelector";
import type { CalendarMode } from "../CalendarDateSelector";
import { useTimeSheetFilters } from "../time-sheet/hooks/useTImeSheetFilters";
import CalendarJobs from "./CalendarJobs";
import { WeekJobs } from "./weekJobs";
import { SASSelectMenuStyles } from "../ui/styles/inputStyles";
import { colors } from "../ui/colors";
import { TodayJobsCalendar } from "./TodayJobsCalendar";
import { RiveByKey } from "@components/riveAnimation";
import { useJobEventsData } from "./useJobDatas";
import { EventJobModal } from "./jobs-calendar/components/calendar-container/modal/components/components/EventJobModal";
import { getStatusPalette } from "./jobs-calendar/components/calendar-container/modal/helpers/normalizedColorsCalendar";
import { BaseModalComponent } from "@components/common/BaseModalComponent";
import { JobsCreate } from "./JobCreate/Jobs.layout";
import { CalendarMonth, Close } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { resetJobCreateStepper } from "../../redux/features/jobCreateSlice";
import { useModals } from "../../context/ModalContext";
import { JobLayout } from "../jobs/jobs-list/jobs-layout/Job.layout";
import { JobDetModal } from "../jobs/jobs-list/job-card/JobDetModal";
import { jobOverlapsDay, jobOverlapsRange } from "./utils/jobOverlapsRange";
import { getCompanyDateRange, getCompanyMonthRange, getCompanyToday } from "./utils/jobOverlapsRange";
import { useAppSelector } from "../../redux/hooks/redux";
import { selectCurrentCompany } from "../../redux/features/user/selectors/userState.selectors";
import { DateTime, IANAZone } from "luxon";
import { ScheduleConflictsPanel, ScheduleConflict } from "./ScheduleConflictsPanel";

const RangeSelectionPreview = ({ start, end }: { start: Date | null; end: Date | null }) => {
  const formatDate = (date: Date | null) => {
    if (!date) return "Select date";
    return format(date, "MMM dd, yyyy");
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        minHeight: 180,
        borderRadius: "16px",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        px: 3,
        py: 2,
        boxSizing: "border-box",
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: "var(--fx-surface-textsecondary, #64748B)",
          fontWeight: 900,
          letterSpacing: "0.08em",
          mb: 1,
          textAlign: "center",
        }}
      >
        Range Selection
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Box
          sx={{
            borderRadius: "14px",
            bgcolor: "rgba(255, 137, 18, 0.08)",
            border: "1px solid rgba(255, 137, 18, 0.18)",
            p: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "var(--fx-surface-texttertiary, #94A3B8)", mb: 0.5 }}>From</Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 900, color: colors.orange.base }}>{formatDate(start)}</Typography>
        </Box>

        <Box
          sx={{
            borderRadius: "14px",
            bgcolor: end ? "rgba(255, 137, 18, 0.08)" : "rgba(148, 163, 184, 0.08)",
            border: end ? "1px solid rgba(255, 137, 18, 0.18)" : "1px dashed rgba(148, 163, 184, 0.35)",
            p: 1,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "var(--fx-surface-texttertiary, #94A3B8)", mb: 0.5 }}>To</Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 900, color: end ? colors.orange.base : "#94a3b8" }}>{formatDate(end)}</Typography>
        </Box>
      </Box>

      <Typography sx={{ mt: 2, fontSize: "0.75rem", color: "var(--fx-surface-texttertiary, #94A3B8)", textAlign: "center", fontWeight: 600 }}>
        {!start ? "Choose when the range should start." : !end ? "Great — now choose when the range should end." : "Range selected"}
      </Typography>
    </Paper>
  );
};

interface CalendarJobsCustomProps {
  defaultMode?: CalendarMode;
  initialDateProp?: string | Date | null;
  initialJobIdProp?: string | number | null;
  disableTimelineJobOpen?: boolean;
  restoreModalKey?: string;
}

const buildEventModalArg = (job: any) => {
  const statusPalette = getStatusPalette(job?.status);
  const jobId = job?.jobId || job?.serviceOrderId || job?.id;

  return {
    event: {
      title: job?.title,
      extendedProps: {
        status: job?.status,
        workType: job?.event?.mode || "Job",
        event: {
          ...job?.event,
          uid: job?.eventUid || job?.event?.uid || job?.id,
          startDate: job?.startDate || job?.event?.startDate || job?.date,
          endDate: job?.endDate || job?.event?.endDate,
        },
        workforce: job?.workforce || [],
        coverageArea: job?.coverageArea,
        jobId,
        color: statusPalette?.color || getTypeColor(job?.type || "residential"),
        quote: job?.quote,
        client: job?.client,
        job: {
          ...job,
          id: jobId,
          alias: job?.alias || job?.title,
        },
      },
    },
  };
};

const jobMatchesCalendarId = (job: any, targetId?: string | number | null) => {
  if (!targetId) return false;

  const target = String(targetId);
  const candidates = [
    job?.id,
    job?.jobId,
    job?.serviceOrderId,
    job?.eventUid,
    job?.uid,
    job?.event?.id,
    job?.event?.uid,
    job?.visitId,
    job?.visit?.id,
  ];

  return candidates.some((candidate) => candidate !== undefined && candidate !== null && String(candidate) === target);
};

const getCalendarJobFocusId = (job: any, fallback?: string | number | null) =>
  job?.id ?? job?.eventUid ?? job?.event?.uid ?? job?.uid ?? fallback ?? null;

export const CalendarJobsCustom = ({
  defaultMode = "day",
  initialDateProp = null,
  initialJobIdProp = null,
  disableTimelineJobOpen = false,
  restoreModalKey = "Dashboard-JobsCalendar-Business",
}: CalendarJobsCustomProps) => {
  const dispatch = useDispatch();
  const companyTimeZone = useAppSelector(selectCurrentCompany)?.timeZone || "UTC";
  const { modals, registerModal } = useModals();
  const initialDate = useMemo(() => {
    if (initialDateProp) {
      const parsedDate = typeof initialDateProp === "string" ? parseISO(initialDateProp) : new Date(initialDateProp);
      parsedDate.setHours(0, 0, 0, 0);
      return parsedDate;
    }

    const today = getCompanyToday(companyTimeZone);
    today.setHours(0, 0, 0, 0);
    return today;
  }, [initialDateProp, companyTimeZone]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [viewBaseDate, setViewBaseDate] = useState<Date>(initialDate);
  const [apiBaseDate, setApiBaseDate] = useState<Date>(initialDate);
  const [apiRangeOverride, setApiRangeOverride] = useState<{ start: Date; end: Date } | null>(null);

  const [selectedJobId, setSelectedJobId] = useState<string | number | null>(initialJobIdProp);
  const [selectedJobSnapshot, setSelectedJobSnapshot] = useState<any | null>(null);
  const [showBanner, setShowBanner] = useState<boolean>(!!initialJobIdProp);
  const [focusScrollTick, setFocusScrollTick] = useState(0);

  const { setDateRange } = useTimeSheetFilters();
  const { mainMode, range, isRangePicker, showCard } = useCalendarDateSelectorState();
  const { setPayload } = useSetCalendarDateSelector();
  const [jobFilter, setJobFilter] = useState("all");

  const selectedDayForDayMode = selectedDate || viewBaseDate || initialDate;

  const [appliedRange, setAppliedRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });

  const jobsListRef = useRef<HTMLDivElement | null>(null);
  const appliedInitialJobRef = useRef<string | number | null>(null);
  const ignoreInitialJobRef = useRef(false);

  useEffect(() => {
    ignoreInitialJobRef.current = false;
    appliedInitialJobRef.current = null;
  }, [initialJobIdProp]);

  const updateApiBaseDateIfNeeded = (date: Date) => {
    setApiBaseDate((prev) => {
      const sameMonth = prev.getFullYear() === date.getFullYear() && prev.getMonth() === date.getMonth();
      return sameMonth ? prev : date;
    });
  };

  const apiRange = useMemo(() => {
    if (apiRangeOverride) {
      return getCompanyDateRange(apiRangeOverride.start, apiRangeOverride.end, companyTimeZone);
    }

    return getCompanyMonthRange(apiBaseDate, companyTimeZone);
  }, [apiBaseDate, apiRangeOverride, companyTimeZone]);

  const { jobsReal, isLoading } = useJobEventsData(apiRange.start, apiRange.end);

  const onCloseModalRestart = () => {
    dispatch(resetJobCreateStepper());
  };

  const filteredJobsByType = useMemo(() => {
    const currentFilter = jobFilter.toLowerCase();
    return currentFilter === "all" ? jobsReal : jobsReal.filter((job) => job.type?.toLowerCase() === currentFilter);
  }, [jobsReal, jobFilter]);

  const handleMonthDaySelect = (date: Date) => {
    ignoreInitialJobRef.current = true;
    setApiRangeOverride(null);

    const nextDate = new Date(date);
    nextDate.setHours(0, 0, 0, 0);

    setViewBaseDate(nextDate);
    updateApiBaseDateIfNeeded(nextDate);

    setSelectedDate((prev) => (prev && isSameDay(prev, nextDate) ? null : nextDate));
    setSelectedJobId(null);
    setSelectedJobSnapshot(null);
    setShowBanner(false);

    setTimeout(() => {
      jobsListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleTimelineJobSelect = (job: any) => {
    ignoreInitialJobRef.current = true;
    appliedInitialJobRef.current = initialJobIdProp ?? null;

    setSelectedJobId(getCalendarJobFocusId(job));
    setSelectedJobSnapshot(job);
    setShowBanner(false);
    setFocusScrollTick((tick) => tick + 1);
  };

  const handleConflictSelect = (conflict: ScheduleConflict) => {
    const zone = IANAZone.isValidZone(companyTimeZone) ? companyTimeZone : "UTC";
    const local = DateTime.fromISO(conflict.startDate, { setZone: true }).setZone(zone);
    const visitDay = new Date(local.year, local.month - 1, local.day);
    ignoreInitialJobRef.current = true;
    setApiRangeOverride(null);
    setApiBaseDate(visitDay);
    setViewBaseDate(visitDay);
    setSelectedDate(visitDay);
    setSelectedJobId(conflict.visitUid);
    setSelectedJobSnapshot(null);
    setShowBanner(false);
    setFocusScrollTick((tick) => tick + 1);
    setPayload({
      viewDate: visitDay,
      range: {
        start: new Date(visitDay.getFullYear(), visitDay.getMonth(), visitDay.getDate(), 0, 0, 0, 0),
        end: new Date(visitDay.getFullYear(), visitDay.getMonth(), visitDay.getDate(), 23, 59, 59, 999),
      },
      isRangePicker: false,
      showCard: false,
    });
  };

  useEffect(() => {
    if (isLoading || !initialJobIdProp || jobsReal.length === 0) return;
    if (ignoreInitialJobRef.current) return;
    if (appliedInitialJobRef.current === initialJobIdProp) return;

    const targetJob = jobsReal.find((j) => jobMatchesCalendarId(j, initialJobIdProp));

    if (!targetJob?.date) return;

    const jobDate = parseISO(targetJob.date);
    jobDate.setHours(0, 0, 0, 0);

    appliedInitialJobRef.current = initialJobIdProp;

    setApiRangeOverride(null);
    setApiBaseDate(jobDate);
    setViewBaseDate(jobDate);
    setSelectedJobId(getCalendarJobFocusId(targetJob, initialJobIdProp));
    setSelectedJobSnapshot(targetJob);
    setShowBanner(true);
    setSelectedDate(jobDate);
    setFocusScrollTick((tick) => tick + 1);
    setPayload({
      viewDate: jobDate,
      range: {
        start: new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate(), 0, 0, 0, 0),
        end: new Date(jobDate.getFullYear(), jobDate.getMonth(), jobDate.getDate(), 23, 59, 59, 999),
      },
      isRangePicker: false,
      showCard: false,
    });

    setTimeout(() => {
      jobsListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  }, [isLoading, initialJobIdProp, jobsReal, setPayload]);

  const getTodayRangeByMode = () => {
    const today = getCompanyToday(companyTimeZone);
    today.setHours(0, 0, 0, 0);

    if (mainMode === "day") {
      const start = new Date(today);
      start.setHours(0, 0, 0, 0);

      const end = new Date(today);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mainMode === "month") {
      return {
        start: startOfMonth(today),
        end: endOfMonth(today),
      };
    }

    return {
      start: startOfWeek(today, { weekStartsOn: 0 }),
      end: endOfWeek(today, { weekStartsOn: 0 }),
    };
  };

  const handleResetToToday = () => {
    ignoreInitialJobRef.current = true;

    const today = getCompanyToday(companyTimeZone);
    today.setHours(0, 0, 0, 0);

    const { start, end } = getTodayRangeByMode();

    setApiRangeOverride(null);
    setApiBaseDate(today);
    setViewBaseDate(today);

    setSelectedJobId(null);
    setSelectedJobSnapshot(null);
    setShowBanner(false);
    setSelectedDate(today);

    setAppliedRange({ start, end });

    setDateRange({
      start: start.toISOString(),
      end: end.toISOString(),
    });

    setPayload({
      viewDate: today,
      range: { start, end },
      isRangePicker: false,
      showCard: false,
    });
  };

  useEffect(() => {
    if (isRangePicker && range.start && range.end) return;

    const baseDate = selectedDate || viewBaseDate || initialDate;

    let start: Date;
    let end: Date;

    if (mainMode === "day") {
      start = new Date(baseDate);
      start.setHours(0, 0, 0, 0);

      end = new Date(baseDate);
      end.setHours(23, 59, 59, 999);
    } else if (mainMode === "month") {
      start = startOfMonth(baseDate);
      end = endOfMonth(baseDate);
    } else {
      start = startOfWeek(baseDate, { weekStartsOn: 0 });
      end = endOfWeek(baseDate, { weekStartsOn: 0 });
    }

    setAppliedRange({ start, end });

    if (!isRangePicker) {
      setApiRangeOverride(null);
      updateApiBaseDateIfNeeded(start);
    }

    setDateRange({
      start: start.toISOString(),
      end: end.toISOString(),
    });
  }, [mainMode, selectedDate, viewBaseDate, initialDate, setDateRange, isRangePicker, range.start, range.end]);

  const handleFilterApplied = (isoRange: any) => {
    ignoreInitialJobRef.current = true;

    const start = isoRange.start ? new Date(isoRange.start) : null;
    const end = isoRange.end ? new Date(isoRange.end) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    setAppliedRange({ start, end });

    setDateRange({
      start: start?.toISOString(),
      end: end?.toISOString(),
    });

    setSelectedJobId(null);
    setSelectedJobSnapshot(null);
    setShowBanner(false);

    if (isRangePicker) {
      if (start && end) {
        setApiRangeOverride({ start, end });
        setViewBaseDate(start);
      }

      setSelectedDate(null);
      return;
    }

    setApiRangeOverride(null);

    if (mainMode === "day" && start) {
      setViewBaseDate(start);
      setSelectedDate(start);
      updateApiBaseDateIfNeeded(start);
      return;
    }

    if (mainMode === "week" && start) {
      const today = getCompanyToday(companyTimeZone);
      today.setHours(0, 0, 0, 0);

      const isTodayInsideWeek = !!end && today >= start && today <= end;
      const nextSelectedDate = isTodayInsideWeek ? today : start;

      setViewBaseDate(nextSelectedDate);
      setSelectedDate(nextSelectedDate);
      updateApiBaseDateIfNeeded(start);
      return;
    }

    if (mainMode === "month" && start) {
      const today = getCompanyToday(companyTimeZone);
      today.setHours(0, 0, 0, 0);

      const isTodayInsideMonth = !!end && today >= start && today <= end;

      setViewBaseDate(start);
      setSelectedDate(isTodayInsideMonth ? today : null);
      updateApiBaseDateIfNeeded(start);
    }
  };

  const dayJobs = useMemo(() => {
    if (mainMode !== "day") return [];

    return filteredJobsByType.filter((job) => {
      return jobOverlapsDay(job, selectedDayForDayMode, companyTimeZone);
    });
  }, [mainMode, filteredJobsByType, selectedDayForDayMode, companyTimeZone]);

  const rangeJobs = useMemo(() => {
    if (!appliedRange.start || !appliedRange.end) return [];

    return filteredJobsByType.filter((job) => {
      if (isRangePicker) {
        return jobOverlapsRange(job, appliedRange.start!, appliedRange.end!, companyTimeZone);
      }

      if (selectedDate) {
        return jobOverlapsDay(job, selectedDate, companyTimeZone);
      }

      return jobOverlapsRange(job, appliedRange.start!, appliedRange.end!, companyTimeZone);
    });
  }, [filteredJobsByType, appliedRange.start, appliedRange.end, selectedDate, isRangePicker, companyTimeZone]);

  const selectedJobData = useMemo(() => {
    return jobsReal.find((j) => jobMatchesCalendarId(j, selectedJobId)) ||
      (isLoading ? selectedJobSnapshot : null);
  }, [selectedJobId, selectedJobSnapshot, jobsReal, isLoading]);

  useEffect(() => {
    if (!registerModal || !restoreModalKey) return;

    const restoreDate = selectedDate || viewBaseDate || initialDate;

    return registerModal(restoreModalKey, {
      content: (
        <CalendarJobsCustom
          defaultMode={mainMode}
          initialDateProp={restoreDate}
          initialJobIdProp={selectedJobId}
          disableTimelineJobOpen={disableTimelineJobOpen}
          restoreModalKey={restoreModalKey}
        />
      ),
    });
  }, [disableTimelineJobOpen, initialDate, mainMode, registerModal, restoreModalKey, selectedDate, selectedJobId, viewBaseDate]);

  useEffect(() => {
    if (!selectedJobId || !jobsListRef.current) return;

    const focusSelectedCard = (behavior: ScrollBehavior) => {
      const jobsList = jobsListRef.current;
      if (!jobsList) return;

      const selectedCard = jobsList.querySelector<HTMLElement>('[data-calendar-job-selected="true"]');
      if (!selectedCard) return;

      const listRect = jobsList.getBoundingClientRect();
      const cardRect = selectedCard.getBoundingClientRect();
      const cardTop = selectedCard.offsetParent === jobsList ? selectedCard.offsetTop : jobsList.scrollTop + cardRect.top - listRect.top;
      const centeredTop = cardTop - (jobsList.clientHeight - selectedCard.offsetHeight) / 2;
      const maxTop = Math.max(0, jobsList.scrollHeight - jobsList.clientHeight);
      const nextTop = Math.max(0, Math.min(maxTop, centeredTop));

      jobsList.scrollTo({
        top: nextTop,
        behavior,
      });
    };

    window.requestAnimationFrame(() => focusSelectedCard("smooth"));
    const retryTimers = [80, 180].map((delay) => window.setTimeout(() => focusSelectedCard("smooth"), delay));

    return () => {
      retryTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [selectedJobId, focusScrollTick, rangeJobs]);

  const isCalendarLoading = isLoading && jobsReal.length === 0;
  const isJobFocusMode = !isRangePicker && !!selectedJobId && (mainMode === "day" || mainMode === "week");
  const showDaySelectionSummary = !isRangePicker && !showCard && mainMode === "day" && !!selectedDate && !!range.start;

  const selectedJobDescription =
    selectedJobId && selectedJobData ? (
      <Box sx={{ height: "100%", "& > .MuiCard-root": { height: "100%" } }}>
        <EventJobModal arg={buildEventModalArg(selectedJobData)} softBorders />
      </Box>
    ) : null;

  const restoredCalendarModalRenderers = useMemo(() => {
    const dynamicModalKeys = modals
      .map((modal) => modal?.key)
      .filter(
        (key): key is string =>
          typeof key === "string" &&
          (/^Calendar-Visit-Stepper-\d+-.+/.test(key) ||
            /^CalendarEvent-JobCard-\d+/.test(key) ||
            /^Jobs-Calendar-\d+-.+/.test(key)),
      );

    return Array.from(new Set(dynamicModalKeys)).map((modalKey) => {
      const visitMatch = modalKey.match(/^Calendar-Visit-Stepper-(\d+)-(.+)$/);
      if (visitMatch) {
        const [, jobId, eventUid] = visitMatch;
        return (
          <BaseModalComponent key={modalKey} modalLabel={`Job ${jobId}`} modalKey={modalKey}>
            <JobLayout parentId={Number(jobId)} currentEventUid={eventUid === "event" ? undefined : eventUid} showCalendar={false} />
          </BaseModalComponent>
        );
      }

      const jobCardMatch = modalKey.match(/^CalendarEvent-JobCard-(\d+)$/);
      if (jobCardMatch) {
        const [, jobId] = jobCardMatch;
        const job = jobsReal.find((item) => String(item?.jobId ?? item?.serviceOrderId ?? item?.id) === String(jobId));
        if (!job) return null;

        return (
          <JobDetModal
            key={modalKey}
            job={job}
            modalKey={modalKey}
            wrapTrigger={false}
            trigger={<Box component="span" sx={{ display: "none" }} />}
          />
        );
      }

      const calendarEventMatch = modalKey.match(/^Jobs-Calendar-(\d+)-(.+)$/);
      if (calendarEventMatch) {
        const [, jobId, eventUid] = calendarEventMatch;
        const job = jobsReal.find(
          (item) =>
            String(item?.eventUid ?? item?.event?.uid ?? item?.id) === String(eventUid) ||
            String(item?.jobId ?? item?.serviceOrderId) === String(jobId),
        );
        if (!job) return null;

        return (
          <BaseModalComponent key={modalKey} defaultSizes={{ width: "600", height: "1000" }} modalLabel={`Job ${jobId}`} modalKey={modalKey}>
            <EventJobModal arg={buildEventModalArg(job)} />
          </BaseModalComponent>
        );
      }

      return null;
    });
  }, [jobsReal, modals]);

  const selectJobPlaceholder = (
    <Box
      sx={{
        height: mainMode === "month" ? "120px" : "120px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed var(--fx-surface-border, #E2E8F0)",
        borderRadius: 4,
        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
        px: 2,
        textAlign: "center",
      }}
    >
      <Typography variant="body2" color="var(--fx-surface-texttertiary, #94A3B8)">
        Select a task to view details
      </Typography>
    </Box>
  );

  const emptyStatePeriod = isRangePicker ? "range" : selectedDate ? "day" : mainMode === "week" ? "week" : mainMode === "month" ? "month" : "day";
  const emptyStateTitle = emptyStatePeriod === "range" ? "This range is wide open" : `Your ${emptyStatePeriod} is wide open`;

  return (
    <Box
      sx={{ px: 4, pt: 4, pb: 0, display: "flex", flexDirection: "column", width: "600", bgcolor: "var(--fx-surface-sunken, #F8FAFC)", overflowY: "auto" }}
    >
      {restoredCalendarModalRenderers}
      {showBanner &&
        initialJobIdProp &&
        (() => {
          const bannerDate = selectedDate || viewBaseDate || initialDate;
          return (
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: "12px",
                bgcolor: "rgba(255, 137, 18, 0.06)",
                border: "1px solid rgba(255, 137, 18, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "1.1rem" }}>
                  <CalendarMonth sx={{ color: colors.orange.base }} />
                </Typography>
                <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500 }}>
                  {mainMode === "day" && (
                    <>
                      Viewing schedule for{" "}
                      <Box component="span" sx={{ color: colors.orange.base, fontWeight: 700 }}>
                        {format(bannerDate, "MMMM dd, yyyy")}
                      </Box>
                    </>
                  )}
                  {mainMode === "week" && (
                    <>
                      Viewing{" "}
                      <Box component="span" sx={{ color: colors.orange.base, fontWeight: 700 }}>
                        Week {format(bannerDate, "w")}
                      </Box>{" "}
                      ({format(startOfWeek(bannerDate, { weekStartsOn: 0 }), "MMM dd")} –{" "}
                      {format(endOfWeek(bannerDate, { weekStartsOn: 0 }), "MMM dd, yyyy")})
                    </>
                  )}
                  {mainMode === "month" && (
                    <>
                      Viewing month of{" "}
                      <Box component="span" sx={{ color: colors.orange.base, fontWeight: 700 }}>
                        {format(bannerDate, "MMMM yyyy")}
                      </Box>
                    </>
                  )}{" "}
                  associated with Job{" "}
                  <Box component="span" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
                    #{initialJobIdProp}
                  </Box>
                </Typography>
              </Box>

              <Box
                component="button"
                onClick={handleResetToToday}
                sx={{
                  background: "none",
                  border: "none",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { color: colors.orange.base },
                }}
              >
                Reset view
              </Box>
            </Box>
          );
        })()}

      <ScheduleConflictsPanel
        startAt={apiRange.start}
        endAt={apiRange.end}
        timeZone={companyTimeZone}
        onSelectVisit={handleConflictSelect}
      />
      <Box sx={{ display: "flex", flex: 1, gap: mainMode === "month" ? 1 : 2, alignItems: "flex-start" }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            pb: 0,
            mb: 0,
            height: "auto",
            width: mainMode === "month" ? "auto" : "60%",
          }}
        >
          <Box sx={{ borderRadius: "12px", display: "flex", justifyContent: "center", width: "100%" }}>
            {isCalendarLoading ? (
              <Box
                sx={{
                  height: 220,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: "16px",
                }}
              >
                <CircularProgress sx={{ color: "#FB8E00" }} />
              </Box>
            ) : isRangePicker && mainMode !== "day" ? (
              <RangeSelectionPreview start={range.start} end={range.end} />
            ) : (
              <>
                {mainMode === "month" && (
                  <Box
                    display="flex"
                    paddingTop={1}
                    paddingX={3}
                    justifyContent="center"
                    borderRadius="16px"
                    bgcolor="var(--fx-surface-base, #FFFFFF)"
                    width="100%"
                    height={393}
                  >
                    <CalendarJobs
                      referenceDate={appliedRange.start || viewBaseDate || initialDate}
                      jobs={filteredJobsByType}
                      timeZone={companyTimeZone}
                      selectedDay={selectedDate}
                      selectedJob={jobFilter}
                      onSelectDay={handleMonthDaySelect}
                    />
                  </Box>
                )}
                {mainMode === "day" && (
                  <TodayJobsCalendar
                    currentDay={selectedDayForDayMode}
                    filter={jobFilter}
                    jobs={dayJobs}
                    isLoading={isCalendarLoading}
                    disableDateFilter
                    highlightedJobId={selectedJobId}
                    compact
                    allowMoreMenuInCompact
                    moreMenuVariant="advanced"
                    disableTimelineJobOpen={disableTimelineJobOpen}
                    disableTimelineScroll
                    showCalendarInEventModal={false}
                    onJobSelect={handleTimelineJobSelect}
                  />
                )}
                {mainMode === "week" && (
                  <WeekJobs
                    selectedJob={jobFilter}
                    jobs={filteredJobsByType.filter((job) =>
                      jobOverlapsRange(job, appliedRange.start || initialDate, appliedRange.end || initialDate, companyTimeZone))}
                    timeZone={companyTimeZone}
                    activeDay={selectedDate}
                    onDayClick={(date) => {
                      ignoreInitialJobRef.current = true;
                      setApiRangeOverride(null);

                      const nextDate = new Date(date);
                      nextDate.setHours(0, 0, 0, 0);

                      setViewBaseDate(nextDate);
                      updateApiBaseDateIfNeeded(nextDate);

                      setSelectedDate((prev) => (prev && isSameDay(prev, nextDate) ? null : nextDate));
                      setSelectedJobId(null);
                      setSelectedJobSnapshot(null);
                      setShowBanner(false);
                    }}
                    referenceDate={appliedRange.start || viewBaseDate || initialDate}
                  />
                )}
              </>
            )}
          </Box>
          <Box sx={{ pt: 1, pb: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box mt={1}>
              <Typography variant="overline" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 900, lineHeight: 1 }}>
                {isRangePicker ? "Selected range" : mainMode === "day" ? "Selected day" : mainMode === "week" ? "Selected week" : "Scheduled tasks"}
              </Typography>
              <Typography sx={{ color: colors.orange.base, fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.2, mt: 0.5 }}>
                {(() => {
                  const taskText = rangeJobs.length === 1 ? "task" : "tasks";

                  if (isRangePicker) {
                    return rangeJobs.length === 0
                      ? "No tasks scheduled for this custom range"
                      : `You have ${rangeJobs.length} ${taskText} in this selected range`;
                  }

                  if (rangeJobs.length === 0) {
                    if (mainMode === "day") {
                      return `No tasks scheduled for ${format(selectedDayForDayMode, "eeee, MMM dd")}`;
                    }
                    if (mainMode === "week" || mainMode === "month") {
                      return selectedDate
                        ? `No tasks scheduled for ${format(selectedDate, "eee, MMM dd")}`
                        : `No tasks scheduled for this ${mainMode}`;
                    }
                    return "No tasks scheduled";
                  }

                  if (mainMode === "day") {
                    return `You have ${rangeJobs.length} ${taskText} for ${format(selectedDayForDayMode, "eeee, MMM dd")}`;
                  }

                  if (mainMode === "week" || mainMode === "month") {
                    return selectedDate
                      ? `You have ${rangeJobs.length} ${taskText} on ${format(selectedDate, "eeee, MMM dd")}`
                      : `You have ${rangeJobs.length} ${taskText} scheduled for this ${mainMode}`;
                  }

                  return `You have ${rangeJobs.length} ${taskText}`;
                })()}
              </Typography>
            </Box>

            {mainMode === "month" && selectedDate && (
              <BaseModalComponent
                onConfirmClose={onCloseModalRestart}
                elementTrigger={
                  <Box
                    component="button"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 2,
                      py: 0.85,
                      border: "none",
                      borderRadius: "10px",
                      bgcolor: colors.orange.base,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    + Create Job · {format(selectedDate, "MMM dd")}
                  </Box>
                }
              >
                <JobsCreate />
              </BaseModalComponent>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "stretch", width: "100%", minWidth: 0 }}>
            <Box
              ref={jobsListRef}
              sx={{
                          display: "flex",
                          mb: 0,
                          position: "relative",
                          flexDirection: "column",
                          gap: 0.5,
                height: rangeJobs.length > 0 ? "auto" : 300,
                maxHeight: rangeJobs.length > 0 ? 340 : 300,
                minHeight: rangeJobs.length > 0 ? 0 : 300,
                flex: "1 1 auto",
                minWidth: 0,
                bgcolor: rangeJobs.length === 0 ? "var(--fx-surface-base, #FFFFFF)" : "transparent",
                borderRadius: "16px",
                          boxSizing: "border-box",
                          overflowY: "auto",
                          overflowX: "hidden",
                          overflowAnchor: "none",
                        }}
            >
              {isCalendarLoading ? (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: "16px" }}>
                  <CircularProgress size={28} sx={{ color: colors.orange.base }} />
                </Box>
              ) : rangeJobs.length > 0 ? (
                rangeJobs.map((job) => {
                  const isSelectedJob = jobMatchesCalendarId(job, selectedJobId);

                  return (
                    <Box key={job.id} data-calendar-job-selected={isSelectedJob ? "true" : undefined} sx={{ width: "100%", flexShrink: 0 }}>
                      <JobCard
                        {...job}
                        jobStatus={job.status}
                        isSelected={isSelectedJob}
                        onOpen={(id) => {
                          ignoreInitialJobRef.current = true;
                          appliedInitialJobRef.current = initialJobIdProp ?? null;

                          if (isSelectedJob) {
                            setSelectedJobId(null);
                            setSelectedJobSnapshot(null);
                            setShowBanner(false);
                            return;
                          }

                          setSelectedJobId(id);
                          setSelectedJobSnapshot(job);
                          setShowBanner(false);
                          setFocusScrollTick((tick) => tick + 1);
                        }}
                        showEditEvent
                      />
                    </Box>
                  );
                })
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    width: "100%",
                    px: 2,
                    py: 3,
                    boxSizing: "border-box",
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "24px",
                      border: "1px solid rgba(255, 152, 0, 0.15)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      width: "100%",
                      maxWidth: 360,
                      px: 2,
                      pt: 1,
                      pb: 2.5,
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden", height: 190 }}>
                      <RiveByKey riveKey="createjob" boxSize={190} />
                    </Box>
                    <Typography variant="h6" sx={{ color: colors.orange.base, fontWeight: 800, fontSize: "1rem", textAlign: "center", mt: -5 }}>
                      {emptyStateTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", fontSize: "0.85rem", opacity: 0.8, mt: 0.5 }}>
                      There are no jobs scheduled for this {emptyStatePeriod}.
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                flex: selectedJobDescription ? "0 0 320px" : "0 0 0px",
                width: selectedJobDescription ? 320 : 0,
                minWidth: selectedJobDescription ? 300 : 0,
                maxWidth: selectedJobDescription ? 320 : 0,
                height: selectedJobDescription ? 340 : 0,
                maxHeight: 340,
                bgcolor: selectedJobDescription ? "var(--fx-surface-base, #FFFFFF)" : "transparent",
                opacity: selectedJobDescription ? 1 : 0,
                visibility: selectedJobDescription ? "visible" : "hidden",
                transform: selectedJobDescription ? "translateX(0)" : "translateX(16px)",
                pointerEvents: selectedJobDescription ? "auto" : "none",
                borderLeft: selectedJobDescription ? "1px solid var(--fx-surface-border, #E2E8F0)" : "1px solid transparent",
                transition: "flex-basis 0.32s ease, width 0.32s ease, min-width 0.32s ease, max-width 0.32s ease, opacity 0.22s ease, transform 0.32s ease, visibility 0.22s ease",
                position: "relative",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {selectedJobDescription && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedJobId(null);
                    setSelectedJobSnapshot(null);
                    setShowBanner(false);
                  }}
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: 2,
                    zIndex: 2,
                    width: 18,
                    height: 18,
                    p: 0,
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    bgcolor: "transparent",
                    "&:hover": {
                      bgcolor: "transparent",
                      color: "var(--fx-surface-textsecondary, #64748B)",
                    },
                  }}
                >
                  <Close sx={{ fontSize: 13 }} />
                </IconButton>
              )}
              {selectedJobDescription}
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: mainMode === "month" || mainMode === "day" || isJobFocusMode ? 240 : "auto", flexShrink: 0, height: "fit-content", textAlign: "center" }}>
          <Box sx={{ mb: 2 }}>
            <Select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} sx={{ ...SASSelectMenuStyles, width: "240px" }}>
              <MenuItem value="all">All Jobs</MenuItem>
              <MenuItem value="residential">Residential</MenuItem>
              <MenuItem value="commercial">Commercial</MenuItem>
            </Select>
          </Box>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              "& > div": { gap: 0.5 },
              "& > div > .MuiPaper-root": {
                minHeight: showDaySelectionSummary ? 205 : mainMode === "month" ? 250 : 300,
                p: mainMode === "month" ? 1.5 : 2,
              },
              "& > div > button": { py: 0.65, minHeight: 34 },
              "& .fc-event, & .fc-daygrid-event, & .fc-daygrid-dot-event": {
                display: "none !important",
              },
            }}
          >
            <CalendarDateSelector
              onFilter={handleFilterApplied}
              modes={["day", "week", "month"]}
              defaultMode={defaultMode}
              width={240}
              hideRangeOnModes={["day"]}
              hideCalendarContent={false}
              showSelectionSummary={showDaySelectionSummary}
            />
          </Box>
          {!selectedJobDescription && !isRangePicker && mainMode === "month" && rangeJobs.length > 0 && (
            <Box sx={{ width: "100%", mt: mainMode === "day" ? 2 : 5.5, textAlign: "left" }}>
              {selectJobPlaceholder}
            </Box>
          )}
        </Box>
      </Box>
      {!selectedJobDescription && (mainMode === "week" || isRangePicker) && rangeJobs.length > 0 && (
        <Box sx={{ width: "100%", pb: 4 }}>{selectJobPlaceholder}</Box>
      )}
    </Box>
  );
};
